import React, { useEffect, useState } from "react";
import { X } from "@phosphor-icons/react";
import { quotes as quotesApi } from "@/api";

export default function QuotePdfPreviewModal({
  open,
  quoteId,
  title = "Devis",
  onClose,
}) {
  const [pdfUrl, setPdfUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !quoteId) {
      setPdfUrl("");
      setLoading(false);
      setError("");
      return undefined;
    }

    let cancelled = false;
    let objectUrl = "";

    setLoading(true);
    setError("");
    setPdfUrl("");

    quotesApi
      .downloadPdf(quoteId)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = window.URL.createObjectURL(blob);
        setPdfUrl(objectUrl);
      })
      .catch(() => {
        if (!cancelled) {
          setError("Prévisualisation du devis impossible.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      if (objectUrl) {
        window.URL.revokeObjectURL(objectUrl);
      }
    };
  }, [open, quoteId]);

  if (!open) return null;

  return (
    <div
      className="acta-modal-overlay fixed inset-0 z-[160] flex items-center justify-center p-3 sm:p-4"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose?.();
      }}
      data-testid="quote-pdf-preview-modal"
    >
      <section
        className="acta-modal-surface flex h-[92dvh] w-full max-w-6xl flex-col overflow-hidden rounded-3xl sm:h-[min(88dvh,860px)]"
        role="dialog"
        aria-modal="true"
        aria-labelledby="quote-pdf-preview-title"
      >
        <header className="flex shrink-0 items-center justify-between gap-4 border-b border-[var(--do-border)] px-4 py-4 sm:px-6">
          <div className="min-w-0">
            <div className="label-eyebrow">Prévisualisation</div>
            <h2
              id="quote-pdf-preview-title"
              className="mt-1 truncate font-heading text-xl font-black text-[var(--do-ink)]"
            >
              {title}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[var(--do-border)] bg-[var(--do-bg-solid)] text-[var(--do-ink)]"
            aria-label="Fermer la prévisualisation du devis"
            data-testid="quote-pdf-preview-close"
          >
            <X size={20} weight="bold" />
          </button>
        </header>

        <div className="min-h-0 flex-1 bg-[var(--do-surface-soft)] p-3 sm:p-4">
          <div className="flex h-full min-h-[320px] items-center justify-center overflow-hidden rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)]">
            {loading ? (
              <div
                className="px-6 text-center text-sm font-bold text-[var(--do-muted)]"
                role="status"
                data-testid="quote-pdf-preview-loading"
              >
                Chargement du devis…
              </div>
            ) : error ? (
              <div
                className="max-w-md px-6 text-center"
                role="alert"
                data-testid="quote-pdf-preview-error"
              >
                <div className="font-black text-[var(--do-ink)]">
                  Prévisualisation indisponible
                </div>
                <p className="mt-2 text-sm text-[var(--do-muted)]">{error}</p>
              </div>
            ) : pdfUrl ? (
              <iframe
                src={pdfUrl}
                title={`Prévisualisation ${title}`}
                className="h-full min-h-[320px] w-full border-0"
                data-testid="quote-pdf-preview-frame"
              />
            ) : null}
          </div>
        </div>
      </section>
    </div>
  );
}
